import { useRef, useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import moment from 'moment/moment'

import { makeStyles } from '@mui/styles'
import {
	IconButton,
	TextField,
	Tooltip,
	Box,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Collapse,
	Typography,
	Grid,
	CircularProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HelpIcon from '@mui/icons-material/Help'
import CancelIcon from '@mui/icons-material/Cancel'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import { useTheme } from '@mui/material/styles'
import { ClearOutlined, SearchOutlined, RefreshOutlined } from '@mui/icons-material'
import { cloneDeep, isEmpty, uniqBy, orderBy, isEqual, differenceWith } from 'lodash'

import SkeletonLoaderSidePanel from '../../../../../custom-components/skeletons/SkeletonLoaderSidePanel'
import TileWrapper from '../../components/TileWrapper'
import MobileUnitsSettingsDialog from './MobileUnitsSettingDialog'
import tileApi from 'apis/disApi/tileApi'
import useMobileUnitsQuery from '../../hooks/useMobileUnitsQuery'
import { useTileDashboard } from '../../dashboard/Dashboard'
import { tileKeys } from '../../hooks/useTileQuery'
import useDashboardQuery from '../../hooks/useDashboardQuery'
import { useAddressHook } from '../../hooks/useAddressHook'

import { IconThemeProvider } from 'custom-components/context/IconThemesContext'
import { useDebounce } from 'utils/hooks/useDebounce'
import { mobileUnitTileFieldIdMap } from 'utils/params/helpers'
import {
	getChildLinkedFieldsByTileKey,
	getDefaultOperator,
	isJson,
	logErrorMessage,
} from '../../../../../utils/functions/helpers'
import { generateRandomID } from '../../helpers'
import { getUserVariables } from 'components/core/services/environmentService'
import { mobileUnitTilePrimaryKeyColumns } from './MobileUnitsForm'
import { getDefaultColumnsByViewType } from 'components/pages/dis-v2/utils/functions'

import { DEFAULT_CHECKED_OPTIONS } from 'components/pages/dis-v2/_components/OtherOptions'
import { useModalDashboardContext } from '../modal-dashboard/helper/modalDashboardContext'

const useStyles = makeStyles(() => ({
	formHeading: {
		display: 'flex',
		alignSelf: 'stretch',
		paddingTop: 0.5,
		paddingBottom: 0.5,
		paddingRight: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.04)',
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0, 0.04)',
		},
		'& .MuiTypography-root': {
			fontWeight: 'bold',
			display: 'block',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			overflow: 'hidden',
		},
	},
	formItem: {
		padding: 0,
		display: 'flex',
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'stretch',
	},
	formMenuListContainer: {
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		flex: '1 1 0',
		overflow: 'hidden',
		background: '#fff',
	},
	formMenuList: {
		paddingTop: 0,
		overflow: 'hidden',
		height: 'inherit',
		'&:not(.loading):hover': {
			overflowY: 'auto',
		},
		'& .MuiListItem-root': {
			flexDirection: 'column',
		},
		'& .MuiCollapse-root': {
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'stretch',
		},
		'& .MuiListItem-container': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		'& .MuiListItem-container:hover .MuiTypography-root': {
			textDecoration: 'underline',
		},
	},
	formMenuChildList: {
		'& ul li:hover .MuiTypography-root': {
			textDecoration: 'underline',
		},
	},
	loadingWrapper: {
		position: 'absolute',
		top: '20%',
		left: '40%',
		zIndex: '99999',
	},
}))

function MobileUnitsTile(props) {
	const { tile } = props

	const [t] = useTranslation('common')
	const classes = useStyles()
	const { environment } = useSelector((state) => state)
	const iconTheme = environment.theme.icons
	const { id: dashboardKey } = useParams()

	const queryClient = useQueryClient()
	const theme = useTheme()

	const { passedIdValue: parentPassedIdValue, modalDashboardSetting: parentModalDashboardSetting } =
		useModalDashboardContext()

	const currentDashboardKey = useMemo(
		() => parentModalDashboardSetting?.dashboardKey ?? dashboardKey,
		[parentModalDashboardSetting, dashboardKey]
	)

	const updateTileMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(currentDashboardKey)),
	})

	const { updateMutation: updateDashboard, dashboardKeyList } = useDashboardQuery({
		dashboardKey: currentDashboardKey,
	})

	const { fetchAddress } = useAddressHook()

	const tileRef = useRef(null)

	const settings = useMemo(() => {
		if (tile?.settings && isJson(tile?.settings)) {
			return JSON.parse(tile?.settings ?? '{}')
		}

		return {}
	}, [tile?.settings])

	const {
		tileWidth,
		connectedDatagridKey,
		primaryKeyList,
		additionalFields,
		otherOptionsChecked,
		repeatReloadMapSeconds = 10,
	} = settings

	const [searchInput, setSearchInput] = useState('')
	const [displayAdditionalFields, setDisplayAdditionalFields] = useState([])
	const [selectedTeams, setSelectedTeams] = useState([])
	const [currentSelectedTeam, setCurrentSelectedTeam] = useState('')
	const [userVariables, setUserVariables] = useState([])

	// data states
	const [collapseGroupKeys, setCollapseGroupKeys] = useState([])
	const expandAllMobileGroups = useMemo(() => {
		if (Array.isArray(otherOptionsChecked)) {
			const matched = otherOptionsChecked?.some((option) =>
				DEFAULT_CHECKED_OPTIONS.includes(option)
			)

			if (matched) {
				return true
			}
		}
		if (!otherOptionsChecked?.includes('expandAllMobileGroups')) {
			return false
		}
		return true
	}, [otherOptionsChecked])

	const {
		isLoading,
		data: groupedData,
		refetchData,
	} = useMobileUnitsQuery({
		tile,
		searchInput,
		expandAllMobileGroups,
		selectedTeams,
		repeatReloadMapSeconds,
	})

	const combinedPrimaryKeyColumns = useMemo(() => {
		const userVariableFields = userVariables.map((variable, index) => ({
			title: variable.title,
			name: `Variable${index + 1}`,
			mobileUnitKey: `variable${index + 1}`,
			hide: false,
		}))
		return [...mobileUnitTilePrimaryKeyColumns, ...userVariableFields]
	}, [userVariables])

	const parentFilters = useMemo(() => {
		if (isEmpty(combinedPrimaryKeyColumns) || isEmpty(parentModalDashboardSetting)) return []

		const linkedFields = getChildLinkedFieldsByTileKey(tile?.key, parentModalDashboardSetting) || []

		const childLinkedFilters = linkedFields
			?.map((field, index) => {
				const column = combinedPrimaryKeyColumns.find((col) => col.name === field)
				if (!column) return null

				const orgType =
					column?.type === 'text' ? 'string' : column?.type === 'decimal' ? 'number' : column?.type
				const defaultOperator = getDefaultOperator(orgType, true)
				return {
					field,
					operator: parentPassedIdValue === '' ? 'isEmpty' : defaultOperator,
					value: parentPassedIdValue,
					type: orgType,
				}
			})
			.filter((item) => !!item)

		return childLinkedFilters
	}, [parentPassedIdValue, parentModalDashboardSetting, combinedPrimaryKeyColumns, tile?.key])

	function applyFilters(items = [], parentFilters = []) {
		if (!Array.isArray(items) || items.length === 0) return []
		if (!Array.isArray(parentFilters) || parentFilters.length === 0) return items

		return items.filter((item) =>
			parentFilters.every((filter) => {
				const { field, operator, value } = filter
				const allVariableWithValue = combinedPrimaryKeyColumns
					.map((column) => {
						return {
							title: column.title,
							value: item[column.mobileUnitKey] || '',
						}
					})
					.filter((item) => item.value)

				let fieldValue
				const matchVar = allVariableWithValue.find(
					(v) =>
						v.title === field ||
						v.title.replace(/\s+/g, '_') === field ||
						v.title === field.replace(/\s+/g, '_')
				)
				if (matchVar) fieldValue = matchVar.value

				if (operator === 'isEmpty') {
					return fieldValue === null || fieldValue === undefined || fieldValue === ''
				}
				return String(fieldValue) === String(value)
			})
		)
	}

	useEffect(() => {
		const fetchUserVariables = async () => {
			try {
				const result = await getUserVariables(environment.apiToken)
				const newData = result?.data || []
				if (isEmpty(newData)) return

				if (!isEqual(newData, userVariables)) {
					setUserVariables(newData)
				}
			} catch (error) {
				logErrorMessage(error)
			}
		}
		fetchUserVariables()
	}, [])

	const filterDevices = useMemo(() => {
		if (isEmpty(groupedData)) return []

		const search = String(searchInput || '').toLowerCase()

		const filterBySearch = (devices) =>
			devices.filter((device) => device?.name?.toLowerCase().includes(search))

		if (!isEmpty(parentFilters) && !isEmpty(combinedPrimaryKeyColumns)) {
			return groupedData
				.map((group) => {
					const afterParent = applyFilters(group.devices || [], parentFilters)
					const afterSearch = search ? filterBySearch(afterParent) : afterParent

					return {
						...group,
						devices: afterSearch,
					}
				})
				.filter((group) => group.devices.length > 0)
		}

		return groupedData
			.map((group) => ({
				...group,
				devices: search ? filterBySearch(group.devices || []) : group.devices || [],
			}))
			.filter((group) => group.devices.length > 0)
	}, [groupedData, searchInput, parentFilters, combinedPrimaryKeyColumns])

	const { onSelectMobileUnit, resetSelectedMobileNumber } = useTileDashboard()
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [selectedDevice, setSelectedDevice] = useState({})
	const [mappedGroupedData, setMappedGroupedData] = useState([])

	useEffect(() => {
		if (expandAllMobileGroups) {
			setCollapseGroupKeys([])
			setSelectedTeams([])
		} else if (!expandAllMobileGroups && mappedGroupedData?.length) {
			const allTeamKeys = mappedGroupedData.map((group) => group.teamKey)
			setCollapseGroupKeys(allTeamKeys)
		}

		setCurrentSelectedTeam('')
	}, [expandAllMobileGroups])

	useEffect(() => {
		if (additionalFields?.length > 0) {
			setDisplayAdditionalFields(additionalFields)
		} else {
			setDisplayAdditionalFields(['Mobile_number', 'Nickname'])
		}
	}, [additionalFields])

	function handleSelectDevice(device) {
		if (selectedDevice?.key === device?.key) {
			setSelectedDevice({})
			return
		}
		setSelectedDevice(device)
	}

	const handleCollapse = (teamKey) => {
		const found = collapseGroupKeys.includes(teamKey)
		if (!found) {
			setCollapseGroupKeys([...collapseGroupKeys, teamKey])
		} else {
			setCollapseGroupKeys(collapseGroupKeys.filter((item) => item !== teamKey))
		}
	}

	const inputRef = useRef()
	const debounceSearchInput = useDebounce(searchInput, 500)

	function onSearchChange(event) {
		event.preventDefault()
		setSearchInput(event.target.value)
	}

	const onSearchClear = (e) => {
		e.preventDefault()
		inputRef.current.value = ''
		setSearchInput('')
	}

	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const handleSubmitSetting = async (data) => {
		try {
			const {
				primaryKeyList,
				connectedDatagridKey,
				additionalFields,
				otherOptionsChecked,
				repeatReloadMapSeconds,
			} = data
			const currentDashboardKeyList = cloneDeep(dashboardKeyList)
			const tileName = tile.i
			const tileKey = tile.key
			const otherDashboardKeys = currentDashboardKeyList.filter(
				(item) => item.tileKey !== tileKey && item.tileName !== tileName
			)
			const primaryKeysToSave = [...otherDashboardKeys]

			if (!isEmpty(primaryKeyList)) {
				const currentColumns = getDefaultColumnsByViewType(tile.type, environment)
				const newPrimaryKeyList = [
					...primaryKeyList.map((item) => {
						const newPrimaryKey = {
							id: generateRandomID(),
							key: item,
							tileName,
							tileKey,
							mobileUnitKey: mobileUnitTileFieldIdMap[item],
						}
						const columnByKey = currentColumns?.find((column) => column.name === item)
						if (columnByKey) {
							newPrimaryKey['columnType'] = columnByKey.type
						}
						return newPrimaryKey
					}),
				]
				const uniqNewPrimaryKeyList = uniqBy(
					newPrimaryKeyList,
					(item) => `${item.key}-${tile.tileName}-${tile.tileKey}`
				)

				// mark delete primary key
				const deletedPrimaryKeyList = currentDashboardKeyList
					.filter((item) => tileName === item.tileName && tileKey === item.tileKey)
					.map((oldItem) => {
						const hasInNewList = uniqNewPrimaryKeyList.find(
							(newItem) =>
								oldItem.key === newItem.key &&
								oldItem.tileName === newItem.tileName &&
								oldItem.tileKey === newItem.tileKey
						)

						if (hasInNewList) return null

						return {
							...oldItem,
							deleted: true,
						}
					})
					.filter((item) => !!item)

				const allTileKeys = [...uniqNewPrimaryKeyList, ...deletedPrimaryKeyList]

				const keepOldPrimaryKeyList = allTileKeys
					.map((item) => {
						const deletedKey = deletedPrimaryKeyList.find(
							(deletedItem) =>
								deletedItem.key === item.key &&
								deletedItem.tileName === item.tileName &&
								deletedItem.tileKey === item.tileKey
						)
						if (Boolean(deletedKey)) {
							return null
						}

						const oldDashboardKeyItem = currentDashboardKeyList.find(
							(oldItem) =>
								oldItem.key === item.key &&
								oldItem.tileName === item.tileName &&
								oldItem.tileKey === item.tileKey
						)
						if (Boolean(oldDashboardKeyItem)) {
							const columnByKey = currentColumns?.find(
								(column) => column.name === oldDashboardKeyItem.key
							)
							if (columnByKey) {
								oldDashboardKeyItem['columnType'] = columnByKey.type
							}
							return { ...oldDashboardKeyItem, old: true }
						}

						const columnByKey = currentColumns?.find((column) => column.name === item.key)
						if (columnByKey) {
							item['columnType'] = columnByKey.type
						}
						return item
					})
					.filter((item) => !!item)

				primaryKeysToSave.push(...keepOldPrimaryKeyList)
			}

			const orderedSaveList = orderBy(
				primaryKeysToSave,
				(item) => `${item.tileName}-${item.tileKey}-${item.id}`
			)

			await Promise.all([
				updateDashboard.mutateAsync({
					key: currentDashboardKey,
					data: {
						variables: JSON.stringify(orderedSaveList),
						additionalFields,
						otherOptionsChecked,
					},
					token: environment.apiToken,
				}),
				updateTileMutation.mutateAsync({
					dashboardKey: currentDashboardKey,
					tileKey: tile.key,
					data: {
						settings: JSON.stringify({
							...settings,
							connectedDatagridKey,
							additionalFields,
							otherOptionsChecked,
							repeatReloadMapSeconds,
						}),
					},
					token: environment.apiToken,
				}),
			])
		} catch (error) {
			logErrorMessage(error)
		} finally {
			resetSelectedMobileNumber()
			handleCloseDialog()
		}
	}

	const handleResizeTileWidth = async (width) => {
		try {
			const editedSettings = JSON.stringify({
				...settings,
				tileWidth: width,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey: currentDashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	const formatTimeDifference = (lastUpdated) => {
		if (isEmpty(lastUpdated)) return ''

		const now = new Date()
		const diffInMs = now - new Date(lastUpdated)
		const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
		const diffInHours = Math.floor(diffInMinutes / 60)
		if (diffInMs < 0) {
			return '0m'
		}

		if (diffInHours > 24) {
			const diffInDays = Math.floor(diffInHours / 24)
			return `${diffInDays}d`
		} else if (diffInHours >= 1) {
			return `${diffInHours}h`
		} else {
			return `${diffInMinutes}m`
		}
	}

	const formateDateTime = (updated) => {
		const hasUserDateTimeFormat = !!environment.userCurrent.time
		const userDateFormat = hasUserDateTimeFormat
			? environment.userCurrent.time.dateFormat.toUpperCase()
			: 'DD/MM/YYYY'

		const userOffset = environment?.userCurrent?.time?.timezone?.offset
		const momentValue = moment.utc(updated)
		return momentValue?.utcOffset(userOffset).format(`${userDateFormat} HH:mm:ss`)
	}

	const getStatusIcon = (dateStr) => {
		if (isEmpty(dateStr)) return

		const diffMinutes = (Date.now() - new Date(dateStr).getTime()) / 60000

		if (diffMinutes < 5) {
			return (
				<Tooltip title="Recent GPS">
					<CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
				</Tooltip>
			)
		} else if (diffMinutes < 30) {
			return (
				<Tooltip title="Stale GPS">
					<HelpIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
				</Tooltip>
			)
		} else {
			return (
				<Tooltip title="Expired GPS">
					<CancelIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />
				</Tooltip>
			)
		}
	}

	const buildMapDevice = async (device) => {
		const mapDevice = {}
		if (!displayAdditionalFields) return mapDevice
		for (const field of displayAdditionalFields) {
			switch (field) {
				case 'Nickname':
					mapDevice['nickname'] = device.name || device.number
					break

				case 'Mobile_number':
					mapDevice['number'] = device.number || ''
					break

				case 'Email':
					mapDevice['email'] = device.email || ''
					break

				case 'Gps_status':
					mapDevice['gpsStatus'] = device.lastTrack?.status || ''
					mapDevice['gpsStatusDate'] = device.lastTrack?.statusDate || ''
					mapDevice['gpsUpdateTime'] = device.lastTrack?.date || ''
					mapDevice['gpsMovement'] = device.lastTrack?.movement || 0
					break

				case 'Address':
					if (device.lastTrack?.position) {
						fetchAddress({
							latitude: device.lastTrack.position.latitude,
							longitude: device.lastTrack.position.longitude,
						}).then((result) => {
							mapDevice['address'] = result || ''
						})
					} else {
						mapDevice['address'] = ''
					}
					break
				case 'Timesheet_status':
					mapDevice['timesheetStatus'] = device.timesheet?.status || ''
					mapDevice['timesheetNote'] = device.timesheet?.note || ''
					mapDevice['timesheetDate'] = device.timesheet?.date || ''
					break

				default: {
					const normalizedKey = field.toLowerCase()
					const foundKey = Object.keys(device).find((k) => k.toLowerCase() === normalizedKey)
					mapDevice[normalizedKey] = foundKey ? device[foundKey] : ''
					break
				}
			}
		}
		return mapDevice
	}

	const firstRenderRef = useRef(true)
	useEffect(() => {
		const processDevices = async () => {
			if (!filterDevices) return

			const updated = await Promise.all(
				filterDevices.map(async (group) => ({
					...group,
					devices: await Promise.all(
						group.devices.map(async (device) => ({
							...device,
							mapped: await buildMapDevice(device),
						}))
					),
				}))
			)
			setMappedGroupedData(updated)
			if (expandAllMobileGroups) {
				setCollapseGroupKeys([])
			} else {
				const allTeamKeys = updated.map((group) => group.teamKey)

				const isFirstRender = firstRenderRef.current
				if (isFirstRender) {
					firstRenderRef.current = false
					setCollapseGroupKeys(allTeamKeys)
					return
				}

				setCollapseGroupKeys(differenceWith(allTeamKeys, selectedTeams))
			}
		}

		processDevices()
	}, [filterDevices, selectedTeams, displayAdditionalFields, expandAllMobileGroups])

	useEffect(() => {
		if (mappedGroupedData.length === 0 || isLoading) return
		const selectedTeam = mappedGroupedData.find((group) => group.teamKey === currentSelectedTeam)
		if (selectedTeam?.alreadyLoadingDevices) {
			handleCollapse(currentSelectedTeam)
			setCurrentSelectedTeam('')
		}
	}, [mappedGroupedData, currentSelectedTeam, isLoading])

	return (
		<IconThemeProvider values={iconTheme}>
			<TileWrapper
				title={tile?.i}
				onSettingClick={handleOpenDialog}
				ref={tileRef}
				isExpandDialogBtn
			>
				<MobileUnitsSettingsDialog
					tileElementWidth={tileRef?.current?.clientWidth}
					defaultTileWidth={tileWidth}
					tile={tile}
					settings={settings}
					isSubmitting={updateTileMutation.isLoading}
					open={settingsOpen}
					onClose={handleCloseDialog}
					onSubmit={handleSubmitSetting}
					onResizeTileWidth={handleResizeTileWidth}
					dashboardKeyList={dashboardKeyList}
					environment={environment}
				/>

				<Box
					sx={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: 30,
						bottom: 0,
						background: '#fff',

						'& .MuiDataGrid-selectedRowCount': {
							opacity: '0 !important',
						},
					}}
				>
					<div style={{ display: 'flex' }}>
						<TextField
							id="search-input"
							ref={inputRef}
							sx={{ mt: 0.5, mb: 0.5, pl: 1, pr: 1 }}
							InputLabelProps={{ shrink: false }}
							placeholder={t('common:misc.search')}
							size="small"
							fullWidth
							value={searchInput}
							onChange={onSearchChange}
							InputProps={{
								endAdornment: debounceSearchInput ? (
									<IconButton aria-label="clear" size="small" edge="end" onClick={onSearchClear}>
										<ClearOutlined fontSize="inherit" />
									</IconButton>
								) : (
									<IconButton aria-label="clear" size="small" edge="end" disabled>
										<SearchOutlined fontSize="inherit" />
									</IconButton>
								),
							}}
						/>
						<Tooltip
							title={`${t('tooltip.refresh')}`}
							arrow
							placement="bottom-start"
							disableInteractive
						>
							<span>
								<IconButton
									aria-label="refresh"
									size="small"
									className={classes.button}
									sx={{ p: 2 }}
									onClick={() => {
										setMappedGroupedData([])
										refetchData()
									}}
								>
									<RefreshOutlined fontSize="inherit" className={classes.icon} />
								</IconButton>
							</span>
						</Tooltip>
					</div>
					<div className={classes.formMenuListContainer}>
						{isLoading && expandAllMobileGroups && <SkeletonLoaderSidePanel />}
						<List
							className={`${classes.formMenuList} ${
								isLoading ? t('common:misc.loading').toLowerCase() : ''
							}`}
						>
							{mappedGroupedData?.map((data, index) => {
								return (
									<ListItem key={data.teamName} disablePadding>
										<ListItemButton
											className={classes.formHeading}
											onClick={() => {
												if (!expandAllMobileGroups) {
													if (!selectedTeams.includes(data.teamKey)) {
														setCurrentSelectedTeam(data.teamKey)
														setSelectedTeams((prev) => [...prev, data.teamKey])
													} else {
														setCurrentSelectedTeam('')
														setSelectedTeams((prev) => prev.filter((item) => item !== data.teamKey))
													}
												} else {
													handleCollapse(data.teamKey)
												}
											}}
										>
											<ListItemText primary={data.teamName} />
											{currentSelectedTeam === data.teamKey && (
												<CircularProgress size={16} style={{ marginLeft: 8 }} />
											)}
										</ListItemButton>
										<Collapse
											id={index}
											in={!collapseGroupKeys.includes(data.teamKey)}
											timeout="auto"
											unmountOnExit
										>
											<List className={classes.formMenuChildList} disablePadding>
												{data.devices?.map((device) => {
													const mappedDevice = device.mapped
													const allVariableWithValue = combinedPrimaryKeyColumns
														.filter((column) => column.name.startsWith('Variable'))
														.map((column) => ({
															title: column.title,
															value: mappedDevice[column.mobileUnitKey] || '',
														}))
														.filter((item) => item.value)

													return (
														<ListItem
															key={device.key}
															className={classes.formItem}
															style={
																device?.key === selectedDevice?.key
																	? { backgroundColor: 'rgba(0,0,0,0.3)' }
																	: {}
															}
															disablePadding
														>
															<ListItemButton
																className={classes.formItemBtn}
																onClick={() => {
																	onSelectMobileUnit(tile.key, connectedDatagridKey, device)
																	handleSelectDevice(device)
																}}
																style={{
																	display: 'flex',
																	justifyContent: 'space-between',
																	alignItems: 'stretch',
																	flexWrap: 'wrap',
																	py: 1,
																}}
															>
																<Grid
																	container
																	alignItems="flex-start"
																	spacing={{ xs: 1, sm: 2, md: 2 }}
																>
																	<Grid item xs={6} sm={3} md={3}>
																		<Box sx={{ pr: 1 }}>
																			{mappedDevice.nickname && (
																				<div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.primary"
																							component="span"
																						>
																							{mappedDevice.nickname || mappedDevice.number}
																						</Typography>
																					</div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.secondary"
																							component="span"
																						>
																							{mappedDevice.number && (
																								<div>{mappedDevice.number}</div>
																							)}
																							{mappedDevice.email && (
																								<Tooltip title={mappedDevice.email}>
																									<Typography
																										variant="body1"
																										color="text.secondary"
																										sx={{
																											width: '135%',
																											whiteSpace: 'nowrap',
																											overflow: 'hidden',
																											textOverflow: 'ellipsis',
																											display: 'block',
																										}}
																									>
																										{mappedDevice.email}
																									</Typography>
																								</Tooltip>
																							)}
																						</Typography>
																					</div>
																				</div>
																			)}
																		</Box>
																	</Grid>

																	<Grid item xs={6} sm={1.5} md={1.5}>
																		<Box sx={{ pr: 1 }}>
																			{mappedDevice.timesheetStatus && (
																				<div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.primary"
																							component="span"
																						>
																							<Tooltip title={mappedDevice.timesheetNote}>
																								<span>{mappedDevice.timesheetStatus}</span>
																							</Tooltip>
																						</Typography>
																					</div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.secondary"
																							component="span"
																						>
																							{formatTimeDifference(mappedDevice.timesheetDate)}
																						</Typography>
																					</div>
																				</div>
																			)}
																		</Box>
																	</Grid>

																	<Grid item xs={6} sm={2.5} md={2.5}>
																		<Box sx={{ pr: 1 }}>
																			{mappedDevice.gpsStatus && (
																				<div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.primary"
																							component="span"
																						>
																							<Tooltip
																								title={formateDateTime(mappedDevice.gpsUpdateTime)}
																							>
																								{mappedDevice.gpsStatus}
																							</Tooltip>
																						</Typography>
																					</div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.secondary"
																							component="span"
																						>
																							{getStatusIcon(mappedDevice.gpsUpdateTime)}
																							{Number(mappedDevice.gpsMovement) > 10 && (
																								<Tooltip title="Slow movement">
																									<DirectionsWalkIcon
																										sx={{
																											color: theme.palette.common.black,
																											fontSize: 18,
																											marginRight: 0.5,
																										}}
																									/>
																								</Tooltip>
																							)}
																							{formatTimeDifference(mappedDevice.gpsStatusDate)}
																						</Typography>
																					</div>
																				</div>
																			)}
																		</Box>
																	</Grid>

																	<Grid item xs={6} sm={3} md={3}>
																		<Box sx={{ pr: 1 }}>
																			{mappedDevice.address && (
																				<div>
																					<div>
																						<Typography
																							variant="body1"
																							color="text.primary"
																							component="span"
																						>
																							<Tooltip
																								title={formatTimeDifference(
																									mappedDevice.gpsUpdateTime
																								)}
																							>
																								<span
																									style={{
																										whiteSpace: 'normal',
																										wordBreak: 'break-word',
																									}}
																								>
																									{mappedDevice.address}
																								</span>
																							</Tooltip>
																						</Typography>
																					</div>
																				</div>
																			)}
																		</Box>
																	</Grid>

																	{allVariableWithValue.length > 0 && (
																		<Grid item xs={6} sm={2} md={2}>
																			<Box sx={{ pr: 1 }}>
																				{allVariableWithValue.map((item, index) => (
																					<div>
																						<Typography
																							variant="body1"
																							color="text.secondary"
																							component="span"
																						>
																							{item.title}:
																						</Typography>{' '}
																						<Typography
																							variant="body1"
																							color="text.primary"
																							component="span"
																						>
																							{item.value}
																						</Typography>
																					</div>
																				))}
																			</Box>
																		</Grid>
																	)}
																</Grid>
															</ListItemButton>
														</ListItem>
													)
												})}
											</List>
										</Collapse>
									</ListItem>
								)
							})}
						</List>
					</div>
				</Box>
			</TileWrapper>
		</IconThemeProvider>
	)
}

export default MobileUnitsTile
